import { Component } from '@theme/component';
import {
  center,
  closest,
  clamp,
  mediaQueryLarge,
  prefersReducedMotion,
  preventDefault,
  viewTransition,
  scheduler,
} from '@theme/utilities';
import { Scroller, scrollIntoView } from '@theme/scrolling';
import { SlideshowSelectEvent } from '@theme/events';

// The threshold for determining visibility of slides.
const SLIDE_VISIBLITY_THRESHOLD = 0.7;

/**
 * Shared viewport observer manager for lazy scroll enablement.
 *
 * Limit the number of compositor layers created by slideshows by only enabling scrolling when the slideshow is in the viewport.
 * Resolves known issues with iOS Safari where too many composition layers will crash the page.
 * When a slideshow is NOT in the viewport, it has overflow: hidden (no compositor layer).
 * When a slideshow enters the viewport, the [in-viewport] attribute is added, enabling scrolling.
 */
class SlideshowViewportObserver {
  /** @type {SlideshowViewportObserver | null} */
  static #instance = null;

  /** @type {IntersectionObserver | null} */
  #observer = null;

  /**
   * Gets the singleton instance
   * @returns {SlideshowViewportObserver}
   */
  static getInstance() {
    if (!this.#instance) {
      this.#instance = new SlideshowViewportObserver();
    }
    return this.#instance;
  }

  /**
   * Registers a slideshow to be observed for viewport visibility
   * @param {Slideshow} slideshow - The slideshow to observe
   */
  observe(slideshow) {
    if (!this.#observer) {
      this.#observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            const slideshowElement = /** @type {Slideshow} */ (entry.target);
            if (entry.isIntersecting) {
              slideshowElement.setAttribute('in-viewport', '');
            } else {
              slideshowElement.removeAttribute('in-viewport');
            }
          }
        },
        {
          rootMargin: '100px',
        }
      );
    }

    this.#observer.observe(slideshow);
  }

  /**
   * Unregisters a slideshow from viewport observation
   * @param {Slideshow} slideshow - The slideshow to unobserve
   */
  unobserve(slideshow) {
    this.#observer?.unobserve(slideshow);
    slideshow.removeAttribute('in-viewport');
  }
}

/**
 * Slideshow custom element that allows sliding between content.
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} scroller
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement[]} [slides]
 * @property {HTMLElement} [current]
 * @property {HTMLElement[]} [thumbnails]
 * @property {HTMLElement[]} [dots]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
export class Slideshow extends Component {
  static get observedAttributes() {
    return ['initial-slide'];
  }

  /**
   * @param {string} name
   * @param {string} oldValue
   * @param {string} newValue
   */
  attributeChangedCallback(name, oldValue, newValue) {
    // Collection page filtering will Morph slideshow galleries in place, updating
    // the slideshow[initial-slide] and slideshow-slide[hidden] attributes.
    // We need to re-select() the slide after the morph is complete, but not before
    // slideshow-slide elements have their [hidden] attribute updated.
    if (name === 'initial-slide' && oldValue !== newValue) {
      queueMicrotask(() => {
        // Only select if the component is connected and initialized
        if (!this.isConnected || !this.#scroll || !this.refs.slides) return;
        const index = parseInt(newValue, 10) || 0;
        const slide_id = this.refs.slides[index]?.getAttribute('slide-id');
        if (slide_id) {
          this.select({ id: slide_id }, undefined, { animate: false });
        }
      });
    }
  }

  requiredRefs = ['scroller'];

  async connectedCallback() {
    super.connectedCallback();

    // Register with shared viewport observer for lazy scroll enablement.
    // This prevents iOS Safari crashes caused by too many compositor layers.
    SlideshowViewportObserver.getInstance().observe(this);

    // Wait for any in-progress view transitions to finish
    if (viewTransition.current) {
      await viewTransition.current;
      // It's possible that the slideshow was disconnected before the view transition finished
      if (!this.isConnected) return;
    }

    const slideCount = this.slides?.length || 0;
    slideCount <= 1 ? this.#setupSlideshowWithoutControls() : this.#setupSlideshow();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    // Unregister from shared viewport observer
    SlideshowViewportObserver.getInstance().unobserve(this);

    if (this.#scroll) {
      const { scroller } = this.refs;
      scroller.removeEventListener('mousedown', this.#handleMouseDown);
      scroller.removeEventListener('wheel', this.#handleBoundaryWheel);
      scroller.removeEventListener('touchstart', this.#handleBoundaryTouchStart);
      scroller.removeEventListener('touchend', this.#handleBoundaryTouchEnd);
      for (const { el } of this.#clones) el.remove();
      this.#clones = [];
      this.#leadClone = null;
      this.#tailClone = null;
      this.#scroll.destroy();
    }

    const slideCount = this.slides?.length || 0;
    if (slideCount > 1) {
      this.removeEventListener('mouseenter', this.suspend);
      this.removeEventListener('mouseleave', this.resume);
      this.removeEventListener('pointerenter', this.#handlePointerEnter);
      document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
    }

    if (this.#resizeObserver) {
      this.#resizeObserver.disconnect();
    }

    if (this.#intersectionObserver) {
      this.#intersectionObserver.disconnect();
      this.#intersectionObserver = null;
    }
  }

  /** Indicates whether the slideshow is nested inside another slideshow. */
  get isNested() {
    return this.parentElement?.closest('slideshow-component') !== null;
  }

  get initialSlide() {
    return this.refs.slides?.[this.initialSlideIndex];
  }

  /**
   * Selects a slide based on the input index.
   * @param {number|string|{id: string}} input - The index or id of the slide to select.
   * @param {Event} [event] - The event that triggered the selection.
   * @param {Object} [options] - The options for the selection.
   * @param {boolean} [options.animate=true] - Whether to animate the selection.
   */
  async select(input, event, options = {}) {
    if (this.#disabled || !this.refs.slides?.length) return;
    if (!this.#scroll) return;

    // Store the actual current slide before any mutations
    const currentSlide = this.slides?.[this.current];

    for (const slide of this.refs.slides) {
      if (slide.hasAttribute('reveal')) {
        slide.removeAttribute('reveal');
        slide.setAttribute('aria-hidden', 'true');
      }
    }

    // Figure out the raw desired index (could be -1 if user is on first slide and clicks prev)
    let requestedIndex = (() => {
      if (typeof input === 'number') return input;
      if (typeof input === 'string') return parseInt(input, 10);
      if ('id' in input) {
        const requestedSlide = this.refs.slides.find((slide) => slide.getAttribute('slide-id') == input.id);

        if (!requestedSlide || !this.slides) return;

        // Force the slide to be revealed if it is hidden
        if (requestedSlide.hasAttribute('hidden')) {
          requestedSlide.setAttribute('reveal', '');
          requestedSlide.setAttribute('aria-hidden', 'false');
        }

        return this.slides.indexOf(requestedSlide);
      }
    })();

    const { current } = this;
    const { slides } = this;

    // Guard checks: no slides, invalid index, or selecting the same slide
    if (!slides?.length || requestedIndex === undefined || isNaN(requestedIndex)) return;

    const requestedSlideElement = slides?.[requestedIndex];
    if (currentSlide === requestedSlideElement) return;

    if (!this.infinite) requestedIndex = clamp(requestedIndex, 0, slides.length - 1);

    event?.preventDefault();

    const { animate = true } = options;
    const lastIndex = slides.length - 1;

    // With edge clones, wrap by sliding one step into the adjacent clone —
    // motion continues in the pressed direction; the scroll-end teleport then
    // lands invisibly on the matching real slide. The teleport sets `current`
    // silently, so announce the real destination here — listeners (e.g. the
    // hero word overlay) never see the clone hop.
    if (animate && !prefersReducedMotion()) {
      const wrapIndex =
        this.#tailClone && requestedIndex > lastIndex && current === lastIndex
          ? 0
          : this.#leadClone && requestedIndex < 0 && current === 0
            ? lastIndex
            : null;

      if (wrapIndex !== null) {
        const wrapSlide = slides[wrapIndex];
        this.#scroll.to(wrapIndex === 0 ? this.#tailClone : this.#leadClone);

        if (wrapSlide) {
          this.dispatchEvent(
            new SlideshowSelectEvent({
              index: wrapIndex,
              previousIndex: current,
              userInitiated: event != null,
              trigger: 'select',
              slide: wrapSlide,
              id: wrapSlide.getAttribute('slide-id'),
            })
          );
          this.#announcedWrapIndex = wrapIndex;
        }
        return;
      }
    }

    // Decide the actual target index (clamp for infinite loop)
    let index = requestedIndex;
    if (requestedIndex < 0) index = lastIndex;
    else if (requestedIndex > lastIndex) index = 0;

    const isAdjacentSlide = Math.abs(index - current) <= 1 && requestedIndex >= 0 && requestedIndex <= lastIndex;
    const { visibleSlides } = this;
    const instant = prefersReducedMotion() || !animate;

    // If jump is more than 1 or we looped, do the placeholder + reorder trick
    if (!instant && !isAdjacentSlide && visibleSlides.length === 1) {
      this.#disabled = true;
      await this.#scroll.finished; // ensure we're not mid-scroll

      const targetSlide = slides[index];
      if (!targetSlide || !currentSlide) return;

      // Create a placeholder in the original DOM position of targetSlide
      const placeholder = document.createElement('slideshow-slide');
      targetSlide.before(placeholder);

      // Decide whether targetSlide goes before or after currentSlide
      // so that we scroll a short distance in the correct direction
      if (requestedIndex < current) {
        currentSlide.before(targetSlide);
      } else {
        currentSlide.after(targetSlide);
      }

      if (current === 0) this.#scroll.to(currentSlide, { instant: true });

      // Once that scroll finishes, restore the DOM
      queueMicrotask(async () => {
        await this.#scroll.finished;
        this.#disabled = false;

        // Restore the slide back to its original position. This triggers a scroll event.
        placeholder.replaceWith(targetSlide);

        // Instantly scroll to the target slide as its position will have changed
        this.#scroll.to(targetSlide, { instant: true });

        // Force Safari to recalculate the timeline state on timeline refresh (after loop)
        requestAnimationFrame(() => {
          this.setAttribute('refreshing-timeline', '');
          requestAnimationFrame(() => {
            this.removeAttribute('refreshing-timeline');
          });
        });
      });
    }

    const slide = slides[index];
    if (!slide) return;

    const previousIndex = this.current;

    slide.setAttribute('aria-hidden', 'false');

    if (this.#scroll) {
      this.#scroll.to(slide, { instant });
    }

    this.current = this.slides?.indexOf(slide) || 0;

    this.#centerSelectedThumbnail(index, instant ? 'instant' : 'smooth');

    this.dispatchEvent(
      new SlideshowSelectEvent({
        index,
        previousIndex,
        userInitiated: event != null,
        trigger: 'select',
        slide,
        id: slide.getAttribute('slide-id'),
      })
    );
  }

  /**
   * Advances to the next slide.
   * @param {Event} [event] - The event that triggered the next slide.
   * @param {Object} [options] - The options for the next slide.
   * @param {boolean} [options.animate=true] - Whether to animate the next slide.
   */
  next(event, options) {
    event?.preventDefault();
    this.select(this.nextIndex, event, options);
  }

  /**
   * Goes back to the previous slide.
   * @param {Event} [event] - The event that triggered the previous slide.
   * @param {Object} [options] - The options for the previous slide.
   * @param {boolean} [options.animate=true] - Whether to animate the previous slide.
   */
  previous(event, options) {
    event?.preventDefault();
    this.select(this.previousIndex, event, options);
  }

  /**
   * Starts automatic slide playback.
   * @param {number} [interval] - The time interval in seconds between slides.
   */
  play(interval = this.autoplayInterval) {
    if (this.#interval) return;

    this.paused = false;

    this.#interval = setInterval(() => {
      if (this.matches(':hover') || document.hidden) return;

      this.next();
    }, interval);
  }

  /**
   * Pauses automatic slide playback.
   */
  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(value) {
    if (value) {
      this.setAttribute('paused', '');
    } else {
      this.removeAttribute('paused');
    }
  }

  /**
   * Suspends automatic slide playback.
   */
  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  /**
   * Resumes automatic slide playback if autoplay is enabled.
   */
  resume() {
    if (!this.autoplay || this.paused) return;

    this.pause();
    this.play();
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);

    if (Number.isNaN(value)) return undefined;

    return value * 1000;
  }

  /**
   * The current slide index.
   * @type {number}
   */
  #current = 0;

  get current() {
    return this.#current;
  }

  /**
   * Sets the current slide index and update the DOM
   * @type {number}
   */
  set current(value) {
    const { current, thumbnails, dots, slides, previous, next } = this.refs;

    this.#current = value;

    if (current) current.textContent = `${value + 1}`;

    for (const controls of [thumbnails, dots]) {
      controls?.forEach((el, i) => el.setAttribute('aria-selected', `${i === value}`));
    }

    if (previous) previous.disabled = Boolean(!this.infinite && value === 0);
    if (next) next.disabled = Boolean(!this.infinite && slides && this.nextIndex >= slides.length);
  }

  get infinite() {
    return this.getAttribute('infinite') != null;
  }

  get visibleSlides() {
    return this.#visibleSlides;
  }

  get previousIndex() {
    const { current, visibleSlides } = this;
    const modifier = visibleSlides.length > 1 ? visibleSlides.length : 1;

    return current - modifier;
  }

  get nextIndex() {
    const { current, visibleSlides } = this;
    const modifier = visibleSlides.length > 1 ? visibleSlides.length : 1;

    return current + modifier;
  }

  get atStart() {
    const { current, slides } = this;

    return slides?.length ? current === 0 : false;
  }

  get atEnd() {
    const { current, slides } = this;

    return slides?.length ? current === slides.length - 1 : false;
  }

  /**
   * Sets the disabled attribute.
   * @param {boolean} value - The value to set the disabled attribute to.
   */
  set disabled(value) {
    this.setAttribute('disabled', String(value));
  }
  /**
   * Whether the slideshow is disabled.
   * @type {boolean}
   */
  get disabled() {
    return (
      this.getAttribute('disabled') === 'true' || (this.hasAttribute('mobile-disabled') && !mediaQueryLarge.matches)
    );
  }

  /**
   * Indicates whether the slideshow is temporarily disabled (e.g., during infinite loop transition).
   * @type {boolean}
   */
  #disabled = false;

  /**
   * The interval ID for automatic playback.
   * @type {number|undefined}
   */
  #interval = undefined;

  /**
   * The Scroller instance that manages scrolling.
   * @type {Scroller}
   */
  #scroll;

  /**
   * The ResizeObserver instance for monitoring scroller size changes
   * @type {ResizeObserver}
   */
  #resizeObserver;

  /**
   * IntersectionObserver for efficient visibility tracking of slides
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  /**
   * Cached visible slides result from IntersectionObserver
   * @type {HTMLElement[]}
   */
  #visibleSlides = [];

  /**
   * Setup the slideshow without controls for zero or one slides
   */
  #setupSlideshowWithoutControls() {
    this.current = 0;
    if (this.hasAttribute('auto-hide-controls')) {
      const { slideshowControls } = this.refs;
      if (slideshowControls instanceof HTMLElement) {
        slideshowControls.hidden = true;
      }
    }

    if (this.refs.slides?.[0]) {
      this.refs.slides[0].setAttribute('aria-hidden', 'false');
    }
  }

  /**
   * Setup the slideshow with controls for when there are multiple slides
   */
  #setupSlideshow() {
    // Setup IntersectionObserver first for efficient visibility tracking
    this.#setupIntersectionObserver();

    // Setup the scroll instance
    const { scroller } = this.refs;
    this.#scroll = new Scroller(scroller, {
      onScroll: this.#handleScroll,
      onScrollStart: this.#onTransitionInit,
      onScrollEnd: this.#onTransitionEnd,
    });

    scroller.addEventListener('mousedown', this.#handleMouseDown);
    scroller.addEventListener('wheel', this.#handleBoundaryWheel, { passive: true });
    scroller.addEventListener('touchstart', this.#handleBoundaryTouchStart, { passive: true });
    scroller.addEventListener('touchend', this.#handleBoundaryTouchEnd, { passive: true });

    this.addEventListener('mouseenter', this.suspend);
    this.addEventListener('mouseleave', this.resume);
    this.addEventListener('pointerenter', this.#handlePointerEnter);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);

    this.#updateControlsVisibility();

    this.disabled = this.isNested || this.disabled;

    this.resume();

    this.#setupLoopClones();

    // Start from 0 so the initial select() below isn't skipped by the
    // same-slide guard; select() sets `current` to the real initial index.
    this.current = 0;

    // Batch reads and writes to the DOM
    scheduler.schedule(() => {
      let visibleSlidesAmount = 0;
      const initialSlideId = this.initialSlide?.getAttribute('slide-id');

      // Wait for next frame to ensure layout is fully calculated before setting initial scroll position
      // This prevents race conditions on Safari mobile when section_width is 'full-width'
      requestAnimationFrame(() => {
        this.#positionAfterLeadClone();
        if (this.initialSlideIndex !== 0 && this.initialSlide) {
          // Section slides (e.g. _slide blocks) carry no slide-id; fall back to
          // selecting by index so `initial-slide` still works without one.
          this.select(initialSlideId ? { id: initialSlideId } : this.initialSlideIndex, undefined, { animate: false });
          visibleSlidesAmount = 1;
        } else {
          visibleSlidesAmount = this.#updateVisibleSlides();
          if (visibleSlidesAmount === 0) {
            this.select(0, undefined, { animate: false });
            visibleSlidesAmount = 1;
          }
        }
      });

      this.#resizeObserver = new ResizeObserver(async () => {
        if (viewTransition.current) await viewTransition.current;

        if (visibleSlidesAmount > 1) {
          this.#updateVisibleSlides();
        }

        if (this.hasAttribute('auto-hide-controls')) {
          this.#updateControlsVisibility();
        }
      });

      this.#resizeObserver.observe(this.refs.slideshowContainer);
    });
  }

  /** Clone of the last slide placed before the first (with-hints looping). */
  #leadClone = null;

  /** Clone of the first slide placed after the last (with-hints looping). */
  #tailClone = null;

  /** All edge clones with their real-slide indices, for teleport mapping. */
  #clones = [];

  /**
   * Circular peek carousel: mirror the last two slides before the first and
   * the first two after the last, so both edges always show a neighbour peek
   * — including while the scroller is settling on a wrap clone. When a scroll
   * lands on a clone, the scroller teleports instantly to the identical real
   * slide. Clones carry no refs, so the component's slide logic ignores them.
   */
  #setupLoopClones() {
    if (!this.infinite || this.isNested) return;
    if (!this.classList.contains('slideshow--with-hints')) return;
    if (window.Shopify?.designMode) return;

    const slides = this.refs.slides;
    if (!slides || slides.length < 2) return;

    const makeClone = (/** @type {number} */ index) => {
      const clone = /** @type {HTMLElement} */ (slides[index].cloneNode(true));
      clone.removeAttribute('ref');
      clone.removeAttribute('id');
      clone.setAttribute('aria-hidden', 'true');
      clone.setAttribute('inert', '');
      clone.setAttribute('data-loop-clone', '');
      for (const el of clone.querySelectorAll('[ref]')) el.removeAttribute('ref');
      for (const el of clone.querySelectorAll('[id]')) el.removeAttribute('id');
      this.#clones.push({ el: clone, twinIndex: index });
      return clone;
    };

    const last = slides.length - 1;
    const depth = Math.min(2, slides.length - 1);

    // Lead side, in original order: [..., last-1, last] before the first slide.
    for (let i = slides.length - depth; i <= last; i++) {
      const clone = makeClone(i);
      slides[0].before(clone);
      if (i === last) this.#leadClone = clone;
    }

    // Tail side, in original order: [first, second, ...] after the last slide.
    for (let i = 0; i < depth; i++) {
      const clone = makeClone(i);
      this.refs.scroller.append(clone);
      if (i === 0) this.#tailClone = clone;
    }
  }

  /**
   * The lead clone shifts all content right; snap the scroller back onto the
   * first real slide before the initial selection runs.
   */
  #positionAfterLeadClone() {
    const first = this.refs.slides?.[0];
    if (this.#leadClone && first && this.#scroll) {
      this.#scroll.to(first, { instant: true });
    }
  }

  /**
   * Returns the clone currently covering the viewport centre, if any.
   * @returns {HTMLElement | null}
   */
  #cloneAtCenter() {
    const { scroller } = this.refs;
    if (!this.#clones.length || !scroller) return null;

    const center = scroller.scrollLeft + scroller.clientWidth / 2;
    return (
      this.#clones.find(
        ({ el }) => center >= el.offsetLeft && center < el.offsetLeft + el.offsetWidth
      ) ?? null
    );
  }

  /** True when a teleport was deferred because a finger was still down. */
  #teleportPending = false;

  /** Bounded rAF retries for teleport writes the engine dropped (iOS). */
  #teleportRetries = 0;

  /**
   * When a scroll settles on a clone, jump instantly to its real twin.
   * Identical pixels on both ends make the jump invisible.
   *
   * iOS Safari hardening: while a finger is down (or WebKit is still
   * driving touch momentum) programmatic scrollLeft writes are silently
   * dropped. The old version pinned `current` to the twin anyway, so the
   * index and the scroll position desynced — users could then swipe to the
   * hard end of the clone strip where the loop died (atEnd checked the
   * desynced index and never wrapped). So: defer while a touch is active,
   * verify the write actually landed before pinning state, and retry on
   * following frames when the engine swallowed it.
   */
  #teleportFromClones() {
    if (this.#boundaryTouchX != null) {
      this.#teleportPending = true;
      return;
    }
    this.#teleportPending = false;

    const { scroller } = this.refs;
    const slides = this.refs.slides;
    if (!slides?.length || !scroller) return;

    const entry = this.#cloneAtCenter();
    if (!entry) return;

    const twinIndex = entry.twinIndex;
    const twin = slides[twinIndex];
    const target = scroller.scrollLeft + (twin.offsetLeft - entry.el.offsetLeft);

    // The scroller has `scroll-behavior: smooth`; without forcing `auto` this
    // jump would animate as a full rewind across the strip.
    const previousBehavior = scroller.style.scrollBehavior;
    scroller.style.scrollBehavior = 'auto';
    scroller.scrollLeft = target;
    scroller.style.scrollBehavior = previousBehavior;

    if (Math.abs(scroller.scrollLeft - target) > 1) {
      if (this.#teleportRetries < 10) {
        this.#teleportRetries += 1;
        requestAnimationFrame(() => this.#teleportFromClones());
      } else {
        this.#teleportRetries = 0;
      }
      return;
    }
    this.#teleportRetries = 0;

    // #sync() can't see clones (IntersectionObserver lags the jump), so pin
    // the index — this also keeps the dots and arrows in step.
    const previousIndex = this.#current;
    this.current = twinIndex;

    // Native trackpad/wheel scrolls land on clones without any select/drag
    // dispatch having fired (#sync is blind to clones) — announce the wrap
    // here unless select()/drag already did.
    if (twinIndex !== previousIndex && twinIndex !== this.#announcedWrapIndex) {
      this.dispatchEvent(
        new SlideshowSelectEvent({
          index: twinIndex,
          previousIndex,
          userInitiated: true,
          trigger: 'scroll',
          slide: twin,
          id: twin.getAttribute('slide-id'),
        })
      );
    }
    this.#announcedWrapIndex = -1;
    this.#scrollWrapAnnounced = false;
  }

  /**
   * Index of the real (non-clone) slide under the viewport centre, or -1
   * when the centre sits over an edge clone or the gap between cells.
   */
  #realSlideIndexAtCenter() {
    const { scroller } = this.refs;
    const slides = this.refs.slides;
    if (!slides?.length || !scroller) return -1;

    const center = scroller.scrollLeft + scroller.clientWidth / 2;
    return slides.findIndex(
      (slide) => center >= slide.offsetLeft && center < slide.offsetLeft + slide.offsetWidth
    );
  }

  /**
   * Callback invoked on user initiated scroll to sync the current slide index
   * and emit a slide change event if the index has changed.
   */
  #handleScroll = () => {
    this.#lastScrollAt = performance.now();
    const previousIndex = this.#current;
    let index = this.#sync();

    // On looping slideshows (edge clones present) #sync trusts
    // IntersectionObserver visibility, which lags fast glides and the
    // instant teleport jump — iOS Safari delivers observations late enough
    // to still report the PRE-wrap slide and fire spurious selects that
    // flip-flopped the hero words (text played twice at the wrap). There,
    // scroll geometry is the truth: exactly one cell occupies the centre.
    if (index !== previousIndex && this.#clones.length) {
      const geo = this.#realSlideIndexAtCenter();
      if (geo !== -1) {
        if (geo !== index) index = this.current = geo;
      } else if (this.#cloneAtCenter()) {
        // Centre is over an edge clone: the announce path below owns this
        // moment; stale IO churn must not fire a competing select.
        index = this.current = previousIndex;
      }
    }

    if (index === previousIndex) {
      // Native scrolls glide into edge clones with no index change (#sync is
      // blind to clones). Announce the twin as soon as a clone covers the
      // scroller centre — the same mid-glide timing normal slides get from
      // the visibility crossing — instead of waiting for the scroll-end
      // teleport, which left the old slide's text frozen over the new image
      // for the whole glide.
      const entry = this.#cloneAtCenter();
      if (entry) {
        const twin = this.slides?.[entry.twinIndex];
        if (twin && entry.twinIndex !== previousIndex && this.#announcedWrapIndex !== entry.twinIndex) {
          this.#announcedWrapIndex = entry.twinIndex;
          this.#scrollWrapAnnounced = true;
          this.dispatchEvent(
            new SlideshowSelectEvent({
              index: entry.twinIndex,
              previousIndex,
              userInitiated: true,
              trigger: 'scroll',
              slide: twin,
              id: twin.getAttribute('slide-id'),
            })
          );
        }
      }
      // NOTE: retraction of a wrap announcement (reversed swipe) happens at
      // scroll end only — see #retractWrapAnnouncement(). Mid-glide the
      // centre crosses gaps and clone edges in engine-specific orders (iOS
      // snap physics wobble); ANY mid-glide revert dispatch here can
      // ping-pong the announcement and double-play the hero words.
      return;
    }

    const slide = this.slides?.[index];
    if (!slide) return;

    this.dispatchEvent(
      new SlideshowSelectEvent({
        index,
        previousIndex,
        userInitiated: true,
        trigger: 'scroll',
        slide,
        id: slide.getAttribute('slide-id'),
      })
    );
    this.#announcedWrapIndex = -1;
    this.#scrollWrapAnnounced = false;
  };

  /**
   * Real slide index already announced by a select()/drag wrap dispatch, so
   * the clone teleport doesn't announce it a second time. -1 when nothing is
   * pending.
   */
  #announcedWrapIndex = -1;

  /**
   * True when the pending announcement came from #handleScroll itself (clone
   * under the centre), so a reversal out of the clone can retract it. Arrow
   * and drag announcements never set this.
   */
  #scrollWrapAnnounced = false;

  /** Timestamp of the most recent scroll event, used to settle boundary gestures. */
  #lastScrollAt = 0;

  /** Guards against momentum wheel events re-triggering a boundary wrap. */
  #boundaryWrapLocked = false;

  /** Touch start X for boundary swipe detection. */
  #boundaryTouchX = null;

  /**
   * Wraps around when the user keeps swiping past the first or last slide.
   * Native scroll physics stop at the edges, so `infinite` needs a gesture
   * nudge: a settled horizontal wheel/trackpad swipe at the boundary loops.
   * @param {WheelEvent} event
   */
  #handleBoundaryWheel = (event) => {
    if (!this.infinite || this.disabled || this.#boundaryWrapLocked) return;
    const { deltaX, deltaY } = event;
    if (Math.abs(deltaX) < 15 || Math.abs(deltaX) <= Math.abs(deltaY)) return;
    if (performance.now() - this.#lastScrollAt < 400) return;
    this.#wrapAtBoundary(deltaX > 0);
  };

  /** @param {TouchEvent} event */
  #handleBoundaryTouchStart = (event) => {
    this.#boundaryTouchX = event.touches[0]?.clientX ?? null;
  };

  /** @param {TouchEvent} event */
  #handleBoundaryTouchEnd = (event) => {
    const startX = this.#boundaryTouchX;
    this.#boundaryTouchX = null;

    if (this.#teleportPending) {
      // A settle happened while the finger was down; run it once the touch
      // is over AND the scroller is at rest. If momentum follows the
      // release, onScrollEnd re-runs the teleport at the real settle —
      // teleporting mid-glide double-steps the wrap.
      setTimeout(() => {
        if (this.#teleportPending && performance.now() - this.#lastScrollAt > 100) this.#teleportFromClones();
      }, 120);
    }

    if (startX == null || !this.infinite || this.disabled || this.#boundaryWrapLocked) return;
    const deltaX = startX - (event.changedTouches[0]?.clientX ?? startX);
    if (Math.abs(deltaX) < 40) return;
    this.#wrapAtBoundary(deltaX > 0);
  };

  /**
   * Fires next()/previous() when the scroller is resting at the matching edge.
   * @param {boolean} forward - true when the gesture pushes toward the end.
   */
  #wrapAtBoundary(forward) {
    const { scroller } = this.refs;
    const maxScroll = scroller.scrollWidth - scroller.clientWidth;
    if (maxScroll <= 0) return;

    // Sitting on a clone: the index-based atEnd/atStart checks below are
    // meaningless there, so never fall through. If the scroll has settled
    // this is a MISSED teleport (iOS dropped the write) — recover the real
    // position. Mid-glide, the scroll-end teleport handles it; recovering
    // early here double-stepped the wrap.
    if (this.#cloneAtCenter()) {
      if (performance.now() - this.#lastScrollAt > 150) this.#teleportFromClones();
      return;
    }

    const atRightEdge = scroller.scrollLeft >= maxScroll - 1;
    const atLeftEdge = scroller.scrollLeft <= 1;

    if (forward && this.atEnd && atRightEdge) {
      this.#lockBoundaryWrap();
      this.next();
    } else if (!forward && this.atStart && atLeftEdge) {
      this.#lockBoundaryWrap();
      this.previous();
    }
  }

  #lockBoundaryWrap() {
    this.#boundaryWrapLocked = true;
    setTimeout(() => {
      this.#boundaryWrapLocked = false;
    }, 1200);
  }

  #onTransitionInit = () => {
    this.setAttribute('transitioning', '');
  };

  /**
   * A wrap was announced mid-glide but the swipe reversed and settled back
   * off the clones — take the announcement back so the hero text returns to
   * the resting slide. Runs at scroll end ONLY: mid-glide reverts ping-pong
   * against the announce and double-play the word transition (iOS snap
   * physics wobble the centre across clone edges and cell gaps).
   */
  #retractWrapAnnouncement() {
    if (!this.#scrollWrapAnnounced) return;
    if (this.#cloneAtCenter()) return; // settled on the clone; teleport owns it

    const announced = this.#announcedWrapIndex;
    this.#announcedWrapIndex = -1;
    this.#scrollWrapAnnounced = false;

    const back = this.slides?.[this.#current];
    if (!back) return;
    this.dispatchEvent(
      new SlideshowSelectEvent({
        index: this.#current,
        previousIndex: announced,
        userInitiated: true,
        trigger: 'scroll',
        slide: back,
        id: back.getAttribute('slide-id'),
      })
    );
  }

  #onTransitionEnd = () => {
    this.#retractWrapAnnouncement();
    this.#teleportFromClones();
    this.#updateVisibleSlides();
    this.removeAttribute('transitioning');
  };

  /**
   * Synchronizes the scroll position and updates the current slide index.
   * @returns {number} The index of the current slide.
   */
  #sync = () => {
    const { slides } = this;
    if (!slides) return (this.current = 0);

    if (!this.#scroll) return (this.current = 0);

    const visibleSlides = this.visibleSlides;

    if (!visibleSlides.length) return this.current;

    const { axis } = this.#scroll;
    const { scroller } = this.refs;
    const centers = visibleSlides.map((slide) => center(slide, axis));
    const referencePoint = visibleSlides.length > 1 ? scroller.getBoundingClientRect()[axis] : center(scroller, axis);
    const closestCenter = closest(centers, referencePoint);
    const closestVisibleSlide = visibleSlides[centers.indexOf(closestCenter)];

    if (!closestVisibleSlide) return (this.current = 0);

    const index = slides.indexOf(closestVisibleSlide);

    return (this.current = index);
  };

  #dragging = false;

  /**
   * Handles the 'mousedown' event to start dragging slides.
   * @param {MouseEvent} event - The mousedown event.
   */
  #handleMouseDown = (event) => {
    const { slides } = this;

    if (!slides || slides.length <= 1) return;
    if (!(event.target instanceof Element)) return;
    if (this.disabled || this.#dragging) return;

    // Check if the event target is within a 3D model interactive element
    // This prevents the slideshow from capturing drag events when interacting with 3D models
    if (event.target.closest('model-viewer')) {
      return;
    }

    event.preventDefault();
    // Store initial position but don't start handling yet
    const { axis } = this.#scroll;
    const startPosition = event[axis];

    const controller = new AbortController();
    const { signal } = controller;
    const startTime = performance.now();
    let previous = startPosition;
    let velocity = 0;
    let moved = false;
    let distanceTravelled = 0;

    this.#dragging = true;

    /**
     * Handles the 'pointermove' event to update the scroll position.
     * @param {PointerEvent} event - The pointermove event.
     */
    const onPointerMove = (event) => {
      const current = event[axis];
      const initialDelta = startPosition - current;

      if (!initialDelta) return;

      if (!moved) {
        moved = true;
        this.setPointerCapture(event.pointerId);

        // Prevent clicks once the user starts dragging
        document.addEventListener('click', preventDefault, { once: true, signal });

        const movingRight = initialDelta < 0;
        const movingLeft = initialDelta > 0;

        // Check if the current slideshow should handle this drag
        const closestSlideshow = this.parentElement?.closest('slideshow-component');
        const isNested = closestSlideshow instanceof Slideshow && closestSlideshow !== this;
        const cannotMoveInDirection = (movingRight && this.atStart) || (movingLeft && this.atEnd);

        // Abort and let the parent slideshow handle the drag if we're moving in a direction where nested slideshow can't move
        if (isNested && cannotMoveInDirection) {
          controller.abort();
          return;
        }

        this.pause();
        this.setAttribute('dragging', '');
      }

      // Stop the event from bubbling up to parent slideshow components
      event.stopImmediatePropagation();

      const delta = previous - current;
      const timeDelta = performance.now() - startTime;
      velocity = Math.round((delta / timeDelta) * 1000);
      previous = current;
      distanceTravelled += Math.abs(delta);

      this.#scroll.by(delta, { instant: true });
    };

    /**
     * Handles the 'pointerup' event to stop dragging slides.
     * @param {PointerEvent} event - The pointerup event.
     */
    const onPointerUp = async (event) => {
      controller.abort();
      const { current, slides } = this;
      const { scroller } = this.refs;

      this.#dragging = false;

      if (!slides?.length || !scroller) return;

      const direction = Math.sign(velocity);
      const next = this.#sync();

      const modifier = current !== next || Math.abs(velocity) < 10 || distanceTravelled < 10 ? 0 : direction;
      const rawIndex = next + modifier;
      const lastIndex = slides.length - 1;

      // Infinite drag past an edge: keep moving into the adjacent clone in the
      // drag direction (released on a clone, or flicked beyond the boundary);
      // the scroll-end teleport then lands on the matching real slide. Like
      // the arrow wrap in select(), the teleport sets `current` silently —
      // announce the real destination so listeners see the change.
      const centerClone = this.#cloneAtCenter();
      const wrapTarget =
        centerClone?.el ??
        (rawIndex < 0 && next === 0
          ? this.#leadClone
          : rawIndex > lastIndex && next === lastIndex
            ? this.#tailClone
            : null);

      if (wrapTarget) {
        const wrapIndex = centerClone
          ? centerClone.twinIndex
          : wrapTarget === this.#leadClone
            ? lastIndex
            : 0;
        const wrapSlide = slides[wrapIndex];
        const wrapPreviousIndex = this.current;

        this.#scroll.to(wrapTarget);
        this.removeAttribute('dragging');
        this.releasePointerCapture(event.pointerId);

        if (wrapSlide && wrapIndex !== wrapPreviousIndex) {
          this.dispatchEvent(
            new SlideshowSelectEvent({
              index: wrapIndex,
              previousIndex: wrapPreviousIndex,
              userInitiated: true,
              trigger: 'drag',
              slide: wrapSlide,
              id: wrapSlide.getAttribute('slide-id'),
            })
          );
          this.#announcedWrapIndex = wrapIndex;
        }

        await this.#scroll.finished;
        if (this.#dragging) return;

        this.#scroll.snap = true;
        this.resume();
        return;
      }

      const newIndex = clamp(rawIndex, 0, lastIndex);

      const newSlide = slides[newIndex];
      const currentIndex = this.current;

      if (!newSlide) throw new Error(`Slide not found at index ${newIndex}`);

      this.#scroll.to(newSlide);

      this.removeAttribute('dragging');
      this.releasePointerCapture(event.pointerId);

      this.#centerSelectedThumbnail(newIndex);

      this.dispatchEvent(
        new SlideshowSelectEvent({
          index: newIndex,
          previousIndex: currentIndex,
          userInitiated: true,
          trigger: 'drag',
          slide: newSlide,
          id: newSlide.getAttribute('slide-id'),
        })
      );

      this.current = newIndex;

      await this.#scroll.finished;

      // It's possible that the user started dragging again before the scroll finished
      if (this.#dragging) return;

      this.#scroll.snap = true;
      this.resume();
    };

    this.#scroll.snap = false;

    document.addEventListener('pointermove', onPointerMove, { signal });
    document.addEventListener('pointerup', onPointerUp, { signal });
    /**
     * pointerDown calls onPointerUp to fix an issue where the first tap-and-drag
     * on the zoom dialog is captured by the pointerMove/pointerUp listeners,
     * sometimes causing the slideshow to change slides unexpectedly
     */
    document.addEventListener('pointerdown', onPointerUp, { signal });
    document.addEventListener('pointercancel', onPointerUp, { signal });
    document.addEventListener('pointercapturelost', onPointerUp, { signal });
  };

  #handlePointerEnter = () => {
    this.setAttribute('actioned', '');
  };

  get slides() {
    return this.refs.slides?.filter((slide) => !slide.hasAttribute('hidden') || slide.hasAttribute('reveal'));
  }

  /**
   * The initial slide index.
   * @type {number}
   */
  get initialSlideIndex() {
    const initialSlide = this.getAttribute('initial-slide');
    if (initialSlide == null) return 0;

    return parseInt(initialSlide, 10);
  }

  /**
   * Pause the slideshow when the page is hidden.
   */
  #handleVisibilityChange = () => (document.hidden ? this.suspend() : this.resume());

  #updateControlsVisibility() {
    if (!this.hasAttribute('auto-hide-controls')) return;

    const { scroller, slideshowControls } = this.refs;

    if (!(slideshowControls instanceof HTMLElement)) return;

    slideshowControls.hidden = scroller.scrollWidth <= scroller.offsetWidth;
  }

  /**
   * Setup IntersectionObserver for efficient visibility tracking of slides
   */
  #setupIntersectionObserver() {
    const { slides, scroller } = this.refs;
    if (!slides?.length) return;

    if (this.#intersectionObserver) {
      this.#intersectionObserver.disconnect();
    }

    this.#intersectionObserver = new IntersectionObserver(
      (entries) => {
        const allEntries = [
          ...entries,
          ...(this.#intersectionObserver ? this.#intersectionObserver.takeRecords() : []),
        ];

        for (const entry of allEntries) {
          const slide = /** @type {HTMLElement} */ (entry.target);
          const isCurrentlyVisible = this.#visibleSlides.includes(slide);
          const shouldBeVisible = entry.intersectionRatio >= SLIDE_VISIBLITY_THRESHOLD;

          if (shouldBeVisible && !isCurrentlyVisible) {
            this.#visibleSlides.push(slide);
          } else if (!shouldBeVisible && isCurrentlyVisible) {
            const index = this.#visibleSlides.indexOf(slide);
            if (index > -1) {
              this.#visibleSlides.splice(index, 1);
            }
          }
        }

        this.#visibleSlides.sort((a, b) => slides.indexOf(a) - slides.indexOf(b));
        this.#updateVisibleSlides();
      },
      {
        root: scroller,
        threshold: SLIDE_VISIBLITY_THRESHOLD,
        // Add small margin to account for sub-pixel rendering
        rootMargin: '1px',
      }
    );

    // Observe all slides - observer will fire initial callback asynchronously
    slides.forEach((slide) => {
      this.#intersectionObserver?.observe(slide);
    });
  }

  /**
   * Centers the selected thumbnail in the thumbnails container
   * @param {number} index - The index of the selected thumbnail
   * @param {ScrollBehavior} [behavior] - The scroll behavior.
   */
  #centerSelectedThumbnail(index, behavior = 'smooth') {
    const selectedThumbnail = this.refs.thumbnails?.[index];
    if (!selectedThumbnail) return;

    const { thumbnailsContainer } = this.refs;
    if (!thumbnailsContainer || !(thumbnailsContainer instanceof HTMLElement)) return;

    const { slideshowControls } = this.refs;
    if (!slideshowControls || !(slideshowControls instanceof HTMLElement)) return;

    scrollIntoView(selectedThumbnail, {
      ancestor: thumbnailsContainer,
      behavior,
      block: 'center',
      inline: 'center',
    });
  }

  #updateVisibleSlides() {
    const { slides } = this;
    if (!slides || !slides.length) return 0;

    const visibleSlides = this.visibleSlides;

    // If the IntersectionObserver reports zero visible slides, then the slideshow has intersected, but no slides meet the visibility threshold.
    // Probably that the slideshow's host is mid-animation or zero-layout, like in an animating modal.
    // Don't stamp aria-hidden="true" on every slide. Set the current slide to the first visible slide.
    if (visibleSlides.length === 0) return 0;

    // Batch writes to the DOM
    scheduler.schedule(() => {
      // Update aria-hidden based on visibility
      slides.forEach((slide) => {
        const isVisible = visibleSlides.includes(slide);
        slide.setAttribute('aria-hidden', `${!isVisible}`);
      });
    });

    return visibleSlides.length;
  }
}

if (!customElements.get('slideshow-component')) {
  customElements.define('slideshow-component', Slideshow);
}
